import React from "react";
import WhatsAppFormattedText from "../whatsapp/WhatsAppFormattedText";
import { MessageBubbleButtonFooter } from "../whatsapp/MessageBubbleButtonFooter";
import { TemplateBubbleButtons } from "../whatsapp/TemplateBubbleButtons";
import { BubbleTimestampContent } from "./MessageBubbleMeta";

function substituteParams(text, params) {
  if (!text || !params) return text || "";
  let result = text;
  params.forEach((p, i) => {
    const value = p.text ?? p;
    result = result.replace(`{{${i + 1}}}`, value);
    if (p.parameter_name) {
      result = result.replace(`{{${p.parameter_name}}}`, value);
    }
  });
  return result;
}

function getComponentByType(components, type) {
  if (!components) return null;
  return components.find((c) => c.type === type || c.type === type.toUpperCase());
}

function getSendParams(sendComponents, type) {
  if (!sendComponents) return null;
  const comp = sendComponents.find(
    (c) => c.type === type.toLowerCase() || c.type === type.toUpperCase()
  );
  return comp?.parameters || null;
}

function buildParamsFromTemplateVars(templateVars, type, tplComponent) {
  if (!templateVars || typeof templateVars !== "object") return null;
  const prefix = type === "header" ? "header_" : "body_";
  const params = [];
  for (let i = 1; i <= 50; i++) {
    const key = `${prefix}${i}`;
    if (templateVars[key] === undefined) break;
    const p = { type: "text", text: templateVars[key] };
    const namedDefs =
      tplComponent?.example?.[`${prefix.slice(0, -1)}_text_named_params`];
    if (namedDefs && namedDefs[i - 1]?.param_name) {
      p.parameter_name = namedDefs[i - 1].param_name;
    }
    params.push(p);
  }
  return params.length > 0 ? params : null;
}

function isHttpUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

function firstHttpUrl(...candidates) {
  for (const value of candidates) {
    if (isHttpUrl(value)) return value.trim();
  }
  return null;
}

/**
 * Prefer the per-send header media (API / campaign payload) over the
 * template's static sample `header_handle`. WhatsApp clients already get
 * the dynamic image; live chat used to always show the sample.
 */
function resolveHeaderMedia(format, sendComponents, templateVars, header) {
  const params = getSendParams(sendComponents, "header") || [];
  const mediaParam = params.find((p) => {
    const t = String(p?.type || "").toLowerCase();
    return t === "image" || t === "video" || t === "document";
  });

  const kind =
    format === "IMAGE" ? "image" : format === "VIDEO" ? "video" : "document";
  const mediaObj = mediaParam?.[kind] || {};

  const vars = templateVars && typeof templateVars === "object" ? templateVars : {};
  const linkKeys =
    format === "IMAGE"
      ? ["header_image_link", "header_image_url"]
      : format === "VIDEO"
        ? ["header_video_link", "header_video_url"]
        : ["header_document_link", "header_document_url"];
  const idKeys =
    format === "IMAGE"
      ? ["header_image_id"]
      : format === "VIDEO"
        ? ["header_video_id"]
        : ["header_document_id"];

  const sentUrl = firstHttpUrl(
    mediaObj.link,
    mediaObj.url,
    ...linkKeys.map((key) => vars[key])
  );
  const sentId = mediaObj.id || idKeys.map((key) => vars[key]).find(Boolean) || null;
  const sampleUrl = firstHttpUrl(header?.example?.header_handle?.[0]);

  return {
    url: sentUrl || (!sentId ? sampleUrl : null),
    filename: mediaObj.filename || vars.header_document_filename || null,
  };
}

const TemplateMessage = ({ message, isOutbound, onPreview, metaFooter }) => {
  const td = message.template_data;
  if (!td) {
    return (
      <div className="text-xs opacity-70 italic">
        Template: {message.template_name || "Unknown"}
      </div>
    );
  }

  const tplJson = td.template_json;
  const sendPayload = td.send_payload;
  const sendComponents =
    sendPayload?.template?.components ||
    sendPayload?.components ||
    [];

  if (!tplJson) {
    const bodyParams = getSendParams(sendComponents, "body");
    return (
      <div className="space-y-1">
        <div className="text-[10px] font-medium opacity-60 uppercase tracking-wide">
          Template: {td.template_name}
        </div>
        {bodyParams && bodyParams.length > 0 && (
          <WhatsAppFormattedText
            text={bodyParams.map((p) => p.text ?? p).join(" ")}
            className="text-sm whitespace-pre-wrap"
          />
        )}
      </div>
    );
  }

  const components = tplJson.components || [];
  const header = getComponentByType(components, "HEADER");
  const body = getComponentByType(components, "BODY");
  const footer = getComponentByType(components, "FOOTER");
  const buttons = getComponentByType(components, "BUTTONS");

  const templateVars = td.template_vars || null;
  const headerParams = getSendParams(sendComponents, "header")
    || buildParamsFromTemplateVars(templateVars, "header", header);
  const bodyParams = getSendParams(sendComponents, "body")
    || buildParamsFromTemplateVars(templateVars, "body", body);

  let bodyText = body?.text || "";
  if (bodyParams) {
    bodyText = substituteParams(bodyText, bodyParams);
  }

  let headerText = header?.text || "";
  if (header?.format === "TEXT" && headerParams) {
    headerText = substituteParams(headerText, headerParams);
  }

  const textColor = "text-[#111b21]";
  const subtextColor = "text-[#667781]";
  const mediaBleed = "rounded-t-[6px] overflow-hidden -mx-2.5 sm:-mx-3 -mt-1.5 sm:-mt-2 mb-1.5";
  const actionLink = "text-[#027eb5]";
  const headerMedia = header
    ? resolveHeaderMedia(header.format, sendComponents, templateVars, header)
    : { url: null, filename: null };
  const headerPreviewUrl = headerMedia.url;

  const templateButtons = buttons?.buttons || [];
  const bubbleButtons = templateButtons.slice(0, 3);
  const hasMoreButtons = templateButtons.length > 3;

  return (
    <div className="space-y-0 min-w-0 max-w-full overflow-hidden">
      {header && (
        <div className={`pb-0.5 ${header.format !== "TEXT" ? "" : ""}`}>
          {header.format === "TEXT" && headerText && (
            <WhatsAppFormattedText
              text={headerText}
              className={`text-[15px] font-semibold leading-snug tracking-tight ${textColor}`}
            />
          )}
          {header.format === "IMAGE" && (
            <div className={mediaBleed}>
              {headerPreviewUrl ? (
                <img
                  src={headerPreviewUrl}
                  alt="Template header"
                  onClick={() =>
                    onPreview?.({
                      type: "image",
                      url: headerPreviewUrl,
                      title: "Template image preview",
                    })
                  }
                  className={`w-full max-h-52 object-cover ${onPreview ? "cursor-zoom-in" : ""}`}
                />
              ) : (
                <div className="h-32 bg-neutral-100 flex items-center justify-center text-3xl">
                  📷
                </div>
              )}
            </div>
          )}
          {header.format === "VIDEO" && (
            <div className={mediaBleed}>
              {headerPreviewUrl ? (
                <video
                  src={headerPreviewUrl}
                  controls
                  onClick={() =>
                    onPreview?.({
                      type: "video",
                      url: headerPreviewUrl,
                      title: "Template video preview",
                    })
                  }
                  className={`w-full max-h-52 ${onPreview ? "cursor-zoom-in" : ""}`}
                />
              ) : (
                <div className="h-32 bg-neutral-100 flex items-center justify-center text-3xl">
                  🎥
                </div>
              )}
            </div>
          )}
          {header.format === "DOCUMENT" && (
            <div className="flex items-center gap-2.5 py-2 px-2 rounded-md bg-[rgba(17,27,33,0.04)] mb-1 border border-[rgba(17,27,33,0.06)]">
              <span className="text-2xl shrink-0">📄</span>
              <div className="min-w-0 flex-1">
                <span className={`text-[11px] font-medium uppercase tracking-wide block ${subtextColor}`}>Document</span>
                {headerMedia.filename && (
                  <span className={`text-xs block truncate ${textColor}`}>{headerMedia.filename}</span>
                )}
                {onPreview && headerPreviewUrl && (
                  <button
                    type="button"
                    onClick={() =>
                      onPreview({
                        type: "document",
                        url: headerPreviewUrl,
                        title: headerMedia.filename || "Template document preview",
                      })
                    }
                    className={`text-xs font-semibold mt-0.5 underline ${actionLink}`}
                  >
                    Preview document
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {(bodyText || footer?.text || metaFooter) && (
        <BubbleTimestampContent>
          {bodyText && (
            <WhatsAppFormattedText
              text={bodyText}
              className={`text-[14.5px] leading-snug whitespace-pre-wrap break-words pt-1 ${textColor}`}
            />
          )}

          {footer?.text && (
            <WhatsAppFormattedText
              text={footer.text}
              className={`text-[13px] mt-2 ${subtextColor}`}
            />
          )}

          {metaFooter}
        </BubbleTimestampContent>
      )}

      {bubbleButtons.length > 0 && (
        <MessageBubbleButtonFooter isOutbound={isOutbound}>
            <TemplateBubbleButtons
              buttons={bubbleButtons}
              variant="chat"
              showTopBorder={false}
            />
            {hasMoreButtons && (
              <p className="text-[12px] text-center text-[#008069] py-2 bg-white rounded-lg border border-[rgba(17,27,33,0.1)] shadow-[0_1px_0.5px_rgba(17,27,33,0.1)]">
                +{templateButtons.length - bubbleButtons.length} more option
                {templateButtons.length - bubbleButtons.length !== 1 ? "s" : ""}
              </p>
            )}
        </MessageBubbleButtonFooter>
      )}
    </div>
  );
};

export default TemplateMessage;
